import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import * as SunCalc from 'suncalc';
import { CITY_LAT, CITY_LON } from '../config';
import { dateFromDayHour, daylightRange } from '../lib/shadow';

/**
 * The arc is drawn 1:1 against its own measured box.
 *
 * It used to have a fixed 600x96 viewBox scaled to fit, which had two costs.
 * Everything inside scaled too, type included: squeezed into a phone's 366px
 * the endcap labels came out around 6px and the sun handle under 4px across --
 * illegible, and far too small to put a finger on. And `meet` letterboxes, so
 * in a box with a different aspect ratio the arc shrank away from its own
 * edges: in the landscape sheet a 630x56 box drew a 242px arc floating in the
 * middle, with the full-width year band beneath it making the gap obvious.
 *
 * Measuring instead means one unit is one pixel, so the type is whatever the
 * stylesheet says, the handle is the size it looks, and the drawing fills the
 * space it was given at every breakpoint.
 */
interface ArcGeometry {
  w: number;
  h: number;
  padX: number;
  horizonY: number;
  peakY: number;
  sunR: number;
}

/** Used for the first paint, before the box has been measured. */
const INITIAL_SIZE = { w: 600, h: 96 };

function geometryFor(w: number, h: number, compact: boolean): ArcGeometry {
  return {
    w,
    h,
    // Wide enough that the sunrise label, which is centered 16px outside the
    // daylight span, still clears the left edge.
    padX: Math.min(Math.max(w * 0.08, 38), 56),
    // The endcap labels sit 15px below the horizon, so it cannot be at the
    // bottom of the box.
    horizonY: h - 22,
    peakY: 10,
    sunR: compact ? 5 : 6,
  };
}

/**
 * Reference maximum solar altitude for NYC (roughly the summer solstice noon
 * elevation). Normalising against a fixed value rather than each day's own
 * maximum is deliberate: it makes the arc visibly flatten in winter and tower
 * in summer, which is the whole point of the control.
 */
const REF_MAX_ALTITUDE = 73;

const MONTH_STARTS = [1, 32, 60, 91, 121, 152, 182, 213, 244, 274, 305, 335];
const MONTH_LABELS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

interface Props {
  dayOfYear: number;
  hour: number;
  onDayChange(day: number): void;
  onHourChange(hour: number): void;
  /** See StatsPanel: `flow` sheds the floating-panel chrome for the sheet. */
  variant?: 'panel' | 'flow';
  /** Narrower arc geometry and tighter readout, for the phone sheet. */
  compact?: boolean;
}

function altitudeAt(dayOfYear: number, hour: number): number {
  const date = dateFromDayHour(dayOfYear, hour);
  const { altitude } = SunCalc.getPosition(date, CITY_LAT, CITY_LON);
  return (altitude * 180) / Math.PI;
}

export function SolarControl({
  dayOfYear,
  hour,
  onDayChange,
  onHourChange,
  variant = 'panel',
  compact = false,
}: Props) {
  const arcRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState(INITIAL_SIZE);

  useLayoutEffect(() => {
    const el = arcRef.current;
    if (!el) return;

    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width > 0 && height > 0) {
        setSize((prev) =>
          Math.abs(prev.w - width) < 0.5 && Math.abs(prev.h - height) < 0.5
            ? prev
            : { w: width, h: height },
        );
      }
    };

    measure(); // before first paint, so the arc is never drawn at the default
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const g = useMemo(() => geometryFor(size.w, size.h, compact), [size, compact]);

  const { min, max } = useMemo(
    () => daylightRange(dateFromDayHour(dayOfYear, 12), CITY_LAT, CITY_LON),
    [dayOfYear],
  );

  const yForAltitude = useCallback(
    (altitude: number) => {
      const t = Math.max(0, altitude) / REF_MAX_ALTITUDE;
      return g.horizonY - t * (g.horizonY - g.peakY);
    },
    [g],
  );

  // The sun's actual path for this day, sampled across daylight hours.
  const path = useMemo(() => {
    const steps = 72;
    const pts: string[] = [];
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const h = min + t * (max - min);
      const x = g.padX + t * (g.w - g.padX * 2);
      const y = yForAltitude(altitudeAt(dayOfYear, h));
      pts.push(`${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`);
    }
    return pts.join(' ');
  }, [dayOfYear, min, max, g, yForAltitude]);

  const clamped = Math.min(Math.max(hour, min), max);
  const t = max > min ? (clamped - min) / (max - min) : 0.5;
  const sunX = g.padX + t * (g.w - g.padX * 2);
  const currentAltitude = altitudeAt(dayOfYear, clamped);
  const sunY = yForAltitude(currentAltitude);

  const scrub = useCallback(
    (clientX: number) => {
      const el = arcRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // The viewBox now matches the box, so a viewBox unit is a CSS pixel and
      // the pointer needs no transform beyond the element's own offset.
      const frac = (clientX - rect.left - g.padX) / (g.w - g.padX * 2);
      onHourChange(min + Math.min(Math.max(frac, 0), 1) * (max - min));
    },
    [min, max, onHourChange, g],
  );

  const date = dateFromDayHour(dayOfYear, clamped);
  const timeLabel = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  const dateLabel = date.toLocaleDateString('en-US', {
    month: compact ? 'short' : 'long',
    day: 'numeric',
  });
  const fmt = (h: number) =>
    dateFromDayHour(dayOfYear, h).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: false,
    });

  return (
    <div className={variant === 'panel' ? 'solar panel enter enter--3' : 'solar solar--flow'}>
      <div className="solar__readout">
        <span className="solar__time">{timeLabel}</span>
        <span className="solar__date">{dateLabel}</span>
        <span className="solar__elev">sun {currentAltitude.toFixed(1)}&deg;</span>
      </div>

      {/*
        Scrubbing is driven from these handlers on both mouse and touch, rather
        than by the range input underneath (which is pointer-events: none and
        exists for the keyboard). A native range maps the pointer across its
        whole width, but the arc's daylight span is inset by padX -- so on touch
        the sun used to sit noticeably away from the finger dragging it.
      */}
      <div
        className="arc"
        ref={arcRef}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          scrub(e.clientX);
        }}
        onPointerMove={(e) => {
          if (e.buttons === 1) scrub(e.clientX);
        }}
      >
        <svg viewBox={`0 0 ${g.w} ${g.h}`} preserveAspectRatio="xMidYMid meet">
          <defs>
            <linearGradient id="sunGradient" x1="0" x2="1">
              <stop offset="0%" stopColor="#e8b54a" stopOpacity="0.15" />
              <stop offset="50%" stopColor="#e8b54a" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#e8b54a" stopOpacity="0.15" />
            </linearGradient>
          </defs>

          <line
            className="arc__horizon"
            x1={g.padX - 14}
            y1={g.horizonY}
            x2={g.w - g.padX + 14}
            y2={g.horizonY}
          />
          <path className="arc__daylight" d={path} />

          <line className="arc__ray" x1={sunX} y1={sunY} x2={sunX} y2={g.horizonY} />
          <circle className="arc__sun" cx={sunX} cy={sunY} r={g.sunR} />

          <text
            className="arc__endcap"
            x={g.padX - 16}
            y={g.horizonY + 15}
            textAnchor="middle"
          >
            {fmt(min)}
          </text>
          <text
            className="arc__endcap"
            x={g.w - g.padX + 16}
            y={g.horizonY + 15}
            textAnchor="middle"
          >
            {fmt(max)}
          </text>
        </svg>

        <input
          className="arc__input"
          type="range"
          min={min}
          max={max}
          step={0.05}
          value={clamped}
          onChange={(e) => onHourChange(parseFloat(e.target.value))}
          aria-label="Time of day"
        />
        <span className="arc__focus" />
      </div>

      <div className="year">
        <div className="year__track">
          <input
            className="year__input"
            type="range"
            min={1}
            max={365}
            step={1}
            value={dayOfYear}
            onChange={(e) => onDayChange(parseInt(e.target.value, 10))}
            aria-label="Day of year"
          />
          <span
            className="year__handle"
            style={{ left: `${((dayOfYear - 1) / 364) * 100}%` }}
          />
        </div>
        <div className="year__months">
          {MONTH_LABELS.map((m, i) => (
            <span key={`${m}-${MONTH_STARTS[i]}`}>{m}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
